import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, ComponentRef, OnDestroy } from '@angular/core';
import { Directive, ElementRef, inject, Input, PLATFORM_ID, Renderer2, ViewContainerRef } from '@angular/core';

import { ShortcodeDataContextService } from '@core-mkt/services/shortcode-data-context/shortcode-data-context.service';
import { AceModeHighlightComponent } from '@core-mkt/shared/components/ace-mode-highlight/ace-mode-highlight.component';
import { PlayAudioButtonComponent } from '@core-mkt/shared/components/play-audio-button/play-audio-button.component';
import { ProductPickerComponent } from '@core-mkt/shared/components/product-picker/product-picker/product-picker.component';
import { TrustpilotStarsComponent } from '@core-mkt/shared/components/trustpilot/trustpilot-stars/trustpilot-stars.component';

@Directive({
  selector: '[shortcodeRenderer]',
  standalone: true,
})
export class ShortcodeRendererDirective implements AfterViewInit, OnDestroy {
  @Input() hasVerticalSeparators = false;
  private observer: MutationObserver | undefined;

  private elementRef = inject(ElementRef);
  private viewContainer = inject(ViewContainerRef);
  private renderer = inject(Renderer2);
  private dataContext = inject(ShortcodeDataContextService);
  private platformId = inject(PLATFORM_ID);

  ngAfterViewInit(): void {
    // Initial scan for existing placeholders
    this.scanAndReplacePlaceholders();

    // Only run MutationObserver in the browser
    if (isPlatformBrowser(this.platformId)) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            this.scanAndReplacePlaceholders();
          }
        });
      });

      this.observer.observe(this.elementRef.nativeElement as Element, {
        childList: true,
        subtree: true,
      });
    }
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private scanAndReplacePlaceholders(): void {
    const productPickerPlaceholders = (this.elementRef.nativeElement as Element).querySelectorAll(
      '.product-picker-placeholder',
    );
    const trustpilotStarsPlaceholders = (this.elementRef.nativeElement as Element).querySelectorAll(
      '.trustpilot-stars-placeholder',
    );
    const aceModeHighlightPlaceholders = (this.elementRef.nativeElement as Element).querySelectorAll(
      '.ace-mode-highlight-placeholder',
    );
    const playAudioPlaceholders = (this.elementRef.nativeElement as Element).querySelectorAll(
      '.play-audio-placeholder',
    );

    productPickerPlaceholders.forEach((placeholder: HTMLElement) => {
      const componentName = placeholder.getAttribute('data-component');
      const dataKey = placeholder.getAttribute('data-key');

      if (componentName === 'productPicker') {
        this.replaceWithProductPicker(placeholder, dataKey);
      }
    });

    trustpilotStarsPlaceholders.forEach((placeholder: HTMLElement) => {
      const componentName = placeholder.getAttribute('data-component');
      const cssClasses = placeholder.getAttribute('data-css-classes');

      if (componentName === 'trustpilotStars') {
        this.replaceWithTrustpilotStars(placeholder, cssClasses);
      }
    });

    aceModeHighlightPlaceholders.forEach((placeholder: HTMLElement) => {
      const componentName = placeholder.getAttribute('data-component');

      if (componentName === 'aceModeHighlight') {
        this.replaceWithAceModeHighlight(placeholder);
      }
    });

    playAudioPlaceholders.forEach((placeholder: HTMLElement) => {
      const componentName = placeholder.getAttribute('data-component');

      if (componentName === 'playAudio') {
        this.replaceWithPlayAudioButton(placeholder);
      }
    });
  }

  private replaceWithProductPicker(placeholder: HTMLElement, dataKey?: string): void {
    try {
      // Create the component
      const componentRef: ComponentRef<ProductPickerComponent> =
        this.viewContainer.createComponent(ProductPickerComponent);

      // Set inputs with data from context if available
      let hasData = false;
      if (dataKey && this.dataContext.hasData(dataKey)) {
        const productPickerData = this.dataContext.getProductPickerData(dataKey);
        if (productPickerData && productPickerData.length > 0) {
          componentRef.instance.data = productPickerData;
          hasData = true;
        }
      }

      // If no specific data key provided, try to use default data
      if (!hasData && this.dataContext.hasData('default')) {
        const defaultData = this.dataContext.getProductPickerData('default');
        if (defaultData && defaultData.length > 0) {
          componentRef.instance.data = defaultData;
          hasData = true;
        }
      }

      // If still no data, log warning but continue (component will handle empty state)
      if (!hasData) {
        // eslint-disable-next-line no-console
        console.warn('ProductPicker shortcode: No data available for key:', dataKey || 'default');
      }

      // Replace the placeholder with the component
      const componentElement = componentRef.location.nativeElement as HTMLElement;
      this.renderer.insertBefore(placeholder.parentNode, componentElement, placeholder);
      this.renderer.removeChild(placeholder.parentNode, placeholder);

      // Ensure the component is properly initialized
      componentRef.changeDetectorRef.markForCheck();
      componentRef.changeDetectorRef.detectChanges();

      // Trigger ngOnChanges if data was set after creation
      if (hasData && componentRef.instance.ngOnChanges) {
        componentRef.instance.ngOnChanges({
          data: {
            currentValue: componentRef.instance.data,
            previousValue: null,
            firstChange: true,
            isFirstChange: () => true,
          },
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating ProductPicker component:', error);
      // Fallback: replace with a simple message
      placeholder.innerHTML = '<div class="product-picker-error">Product picker could not be loaded</div>';
    }
  }

  private replaceWithTrustpilotStars(placeholder: HTMLElement, cssClasses?: string): void {
    try {
      // Create the component
      const componentRef: ComponentRef<TrustpilotStarsComponent> =
        this.viewContainer.createComponent(TrustpilotStarsComponent);

      // Set component data with vertical separator context
      componentRef.instance.data = {
        showTrustpilotStars: true,
        cssClasses: cssClasses || '',
        useStackedLayout: this.hasVerticalSeparators,
      };

      // Replace the placeholder with the component
      const componentElement = componentRef.location.nativeElement as HTMLElement;
      this.renderer.insertBefore(placeholder.parentNode, componentElement, placeholder);
      this.renderer.removeChild(placeholder.parentNode, placeholder);

      // Ensure the component is properly initialized
      componentRef.changeDetectorRef.markForCheck();
      componentRef.changeDetectorRef.detectChanges();
    } catch (error) {
      // Fallback: replace with a simple message
      placeholder.innerHTML = '<div class="trustpilot-stars-error">Trustpilot stars could not be loaded</div>';
    }
  }

  private replaceWithPlayAudioButton(placeholder: HTMLElement): void {
    try {
      const url = placeholder.getAttribute('data-url') || '';
      const label = placeholder.getAttribute('data-label') || 'Play Sample';

      const componentRef: ComponentRef<PlayAudioButtonComponent> =
        this.viewContainer.createComponent(PlayAudioButtonComponent);

      componentRef.instance.url = url;
      componentRef.instance.label = label;

      const componentElement = componentRef.location.nativeElement as HTMLElement;
      this.renderer.insertBefore(placeholder.parentNode, componentElement, placeholder);
      this.renderer.removeChild(placeholder.parentNode, placeholder);

      setTimeout(() => {
        componentRef.changeDetectorRef.detectChanges();
      });
    } catch (error) {
      placeholder.innerHTML = '<div class="play-audio-error">Audio player could not be loaded</div>';
    }
  }

  private replaceWithAceModeHighlight(placeholder: HTMLElement): void {
    try {
      // Set inputs from data attributes
      const headline = placeholder.getAttribute('data-headline');
      const description = placeholder.getAttribute('data-description');
      const linkUrl = placeholder.getAttribute('data-link-url');
      const linkText = placeholder.getAttribute('data-link-text');
      const badgeText = placeholder.getAttribute('data-badge-text');
      const arrowDirection = placeholder.getAttribute('data-arrow-direction');
      const hideOnMobile = placeholder.getAttribute('data-hide-on-mobile');

      // Create the component
      const componentRef: ComponentRef<AceModeHighlightComponent> =
        this.viewContainer.createComponent(AceModeHighlightComponent);

      if (headline) componentRef.instance.headline = headline;
      if (description) componentRef.instance.description = description;
      if (linkUrl) componentRef.instance.linkUrl = linkUrl;
      if (linkText) componentRef.instance.linkText = linkText;
      if (badgeText) componentRef.instance.badgeText = badgeText;
      if (arrowDirection) componentRef.instance.arrowDirection = arrowDirection as 'top' | 'bottom' | 'right' | 'left';
      if (hideOnMobile) componentRef.instance.hideOnMobile = hideOnMobile !== 'false';

      // Replace the placeholder with the component
      const componentElement = componentRef.location.nativeElement as HTMLElement;
      this.renderer.insertBefore(placeholder.parentNode, componentElement, placeholder);
      this.renderer.removeChild(placeholder.parentNode, placeholder);

      // Defer change detection to next cycle to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        componentRef.changeDetectorRef.detectChanges();
      });
    } catch (error) {
      // Fallback: replace with a simple message
      placeholder.innerHTML = '<div class="ace-mode-highlight-error">Ace Mode highlight could not be loaded</div>';
    }
  }
}
